import { DateFormat, Expense, WorkspaceMember } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getUserDisplayName } from '@easy-expense/utils-shared';
import { DoNotFixMeIAmAny } from '@easy-expense/utils-typescript';

import { DateOrder, dateOrderFromFormat, detectDateOrder, parseStatementDate } from './dateParsing';

/**
 * Calculates the difference in days between two date strings (date2 - date1).
 * Returns positive if date2 is after date1, negative if before.
 */
function calculateDaysDifference(date1: string, date2: string): number {
  // Parse date strings as yyyy-MM-dd
  const parts1 = date1.split('-').map(Number);
  const parts2 = date2.split('-').map(Number);

  if (parts1.length !== 3 || parts2.length !== 3) {
    return 0;
  }

  const year1 = parts1[0]!;
  const month1 = parts1[1]!;
  const day1 = parts1[2]!;
  const year2 = parts2[0]!;
  const month2 = parts2[1]!;
  const day2 = parts2[2]!;

  // Create Date objects for calculation (using UTC to avoid timezone issues)
  const date1Obj = new Date(Date.UTC(year1, month1 - 1, day1));
  const date2Obj = new Date(Date.UTC(year2, month2 - 1, day2));

  const diffTime = date2Obj.getTime() - date1Obj.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Subtracts days from a date string in 'yyyy-MM-dd' format.
 * Returns a new date string in 'yyyy-MM-dd' format.
 */
function subtractDaysFromDateString(dateString: string, days: number): string {
  const parts = dateString.split('-').map(Number);
  if (parts.length !== 3) {
    return dateString; // Return original if invalid format
  }

  const year = parts[0]!;
  const month = parts[1]!;
  const day = parts[2]!;
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() - days);

  const newYear = date.getUTCFullYear();
  const newMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
  const newDay = String(date.getUTCDate()).padStart(2, '0');
  return `${newYear}-${newMonth}-${newDay}`;
}

/**
 * Parses a currency value from a string or number, handling common formats.
 * Returns the absolute numeric value for comparison.
 */
function parseCurrencyForComparison(value: string | number | undefined): number | null {
  if (typeof value === 'number') {
    return Math.abs(value);
  }

  if (!value || typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  // Remove currency symbols and thousand separators
  let cleaned = trimmed
    .replace(/[$€£¥₹]/g, '') // Remove common currency symbols
    .replace(/,/g, '') // Remove thousand separators
    .trim();

  // Handle parentheses as negative (accounting format)
  const isNegative = cleaned.startsWith('(') && cleaned.endsWith(')');
  if (isNegative) {
    cleaned = cleaned.slice(1, -1);
  }

  // Remove any remaining non-numeric characters except decimal point and minus sign
  cleaned = cleaned.replace(/[^\d.-]/g, '');

  const parsed = parseFloat(cleaned);

  if (isNaN(parsed)) {
    return null;
  }

  return Math.abs(parsed);
}

export const INTERNAL_HEADERS = [
  'Vendor*',
  'Category*',
  'Payment Method*',
  'Reports*',
  'Description*',
  'Created By*',
  'Receipt Date*',
  'Expense URL*',
  'Attachments*',
];
export type ReconciledStatementRow = {
  'Vendor*'?: string;
  'Category*'?: string;
  'Payment Method*'?: string;
  'Reports*'?: string;
  'Description*'?: string;
  'Created By*'?: string;
  'Receipt Date*'?: string;
  'Expense URL*'?: string;
  'Attachments*'?: string;
  Status?: string;
  Date?: string;
  Vendor?: string;
  Total?: string | number;
  'Tax*'?: number;
  'Subtotal*'?: number;
  'Created By'?: string;
  'Payment Method'?: string;
  Reports?: string;
  Category?: string;
  Description?: string;
  'Expense URL'?: string;
  Attachments?: string;
  [key: string]: string | number | undefined;
};

function getExpenses(statementData: DoNotFixMeIAmAny[], dateHeader: string, dateOrder: DateOrder) {
  const dates = statementData
    .map((statementRow) => parseStatementDate(statementRow[dateHeader], dateOrder))
    .filter((date): date is string => /^\d{4}-\d{2}-\d{2}$/.test(date))
    .sort();
  const allExpenses = Data.expenses.get();

  if (dates.length === 0) {
    return allExpenses;
  }

  const minDate = dates.reduce(
    (min, current) => (current < (min ?? '1900-01-01') ? current : min),
    dates[0],
  );
  const maxDate = dates.reduce(
    (max, current) => (current > (max ?? '3000-01-01') ? current : max),
    dates[0],
  );

  if (!minDate || !maxDate) {
    return allExpenses;
  }

  // Expand the date range to include expenses up to 7 days before the earliest statement date
  // This allows matching expenses dated before the statement date range (e.g., Nov 18 receipt
  // can match Nov 19 statement transaction, since findBestDateMatch allows up to 7 days difference)
  const expandedMinDate = subtractDaysFromDateString(minDate, 7);

  return allExpenses.filter(
    (expense) => expense.date <= maxDate && expense.date >= expandedMinDate,
  );
}

export async function reconcileStatement(params: {
  statementData: DoNotFixMeIAmAny[];
  workspaceMembers: { [key: string]: WorkspaceMember };
  totalHeader: string;
  dateHeader: string;
  token: string;
  showTax: boolean;
  dateFormat?: DateFormat;
}) {
  const { statementData, workspaceMembers, totalHeader, dateHeader, token, showTax, dateFormat } =
    params;
  const matchedExpenseKeys: string[] = [];
  let reconciledStatement: ReconciledStatementRow[] = [];
  const unreconciledStatements: DoNotFixMeIAmAny[] = [];

  // Resolve whether the statement's dates are mm/dd or dd/mm once for the whole
  // file, using unambiguous values when present and the workspace date format
  // setting otherwise (EZ-4027).
  const dateOrder = detectDateOrder(
    statementData.map((row) => row[dateHeader]),
    dateOrderFromFormat(dateFormat),
  );

  // Sort statementData by date before processing
  // Use string comparison like in ReconcileFileUpload.component.tsx
  const sortedStatementData = [...statementData].sort((a, b) => {
    const dateA = parseStatementDate(a[dateHeader], dateOrder);
    const dateB = parseStatementDate(b[dateHeader], dateOrder);
    // Handle invalid dates - put them at the end
    if (!dateA || !/^\d{4}-\d{2}-\d{2}$/.test(dateA)) {
      return 1;
    }
    if (!dateB || !/^\d{4}-\d{2}-\d{2}$/.test(dateB)) {
      return -1;
    }
    // String comparison works for ISO date format (yyyy-MM-dd)
    if (dateA < dateB) {
      return -1;
    }
    if (dateA > dateB) {
      return 1;
    }
    return 0; // Sort ascending (oldest first)
  });

  const expenses = getExpenses(sortedStatementData, dateHeader, dateOrder);

  for (const statementRow of sortedStatementData) {
    // Find all potential matches by total amount, excluding already matched expenses
    const statementTotal = parseCurrencyForComparison(statementRow[totalHeader]);
    if (statementTotal === null) {
      // Skip rows with invalid totals
      reconciledStatement.push({
        Status: 'Missing',
        ...statementRow,
      });
      continue;
    }

    const potentialMatches = expenses.filter((expense) => {
      const expenseTotal = parseCurrencyForComparison(expense.total);
      return (
        expenseTotal !== null &&
        Math.abs(expenseTotal - statementTotal) < 0.01 && // Allow small floating point differences
        !matchedExpenseKeys.includes(expense.key)
      );
    });

    const matchedExpense = findBestDateMatch(potentialMatches, statementRow, dateHeader, dateOrder);

    if (matchedExpense) {
      matchedExpenseKeys.push(matchedExpense.key);
      const vendor = Data.vendors.getByKey(matchedExpense?.vendor)?.value.name;
      const category = Data.expenseCategories.getByKey(matchedExpense?.category)?.value.name;
      const paymentMethod = Data.paymentMethods.getByKey(matchedExpense?.paymentMethod)?.value.name;
      const allReports = Data.reports.get();
      const reports = allReports
        .filter((r) => r.expenses.includes(matchedExpense?.key ?? ''))
        .map((r) => r.name)
        .join(', ');
      const workspaceMember = workspaceMembers[matchedExpense.createdBy];
      const createdByName =
        workspaceMember?.displayName || getUserDisplayName(workspaceMember, 'Anonymous');
      const receiptsURL = getReceiptsURL(matchedExpense);

      const taxFields = showTax
        ? {
            'Tax*': matchedExpense.tax,
            'Subtotal*': matchedExpense.total - (matchedExpense?.tax ?? 0),
          }
        : {};

      reconciledStatement.push({
        Status: 'Reconciled',
        'Vendor*': vendor,
        'Category*': category,
        'Payment Method*': paymentMethod,
        'Reports*': reports,
        'Description*': matchedExpense?.desc,
        'Created By*': createdByName,
        'Receipt Date*': matchedExpense?.date,
        'Expense URL*': `${window.location.origin}/expense/${matchedExpense?.key}`,
        'Attachments*': receiptsURL,
        ...taxFields,
        ...statementRow,
      });
    } else {
      reconciledStatement.push({
        Status: 'Missing',
        ...statementRow,
      });
      unreconciledStatements.push({
        ...statementRow,
      });
    }
  }

  const {
    unmatchedExpenses: splitUnmatchedExpenses,
    reconciledStatements: splitReconciledStatments,
  } = await handleSplitExpenses({
    expenses,
    matchedExpenseKeys,
    unreconciledStatements,
    workspaceMembers,
    totalHeader,
    dateHeader,
    token,
    showTax,
    dateOrder,
  });

  reconciledStatement = [...reconciledStatement, ...splitReconciledStatments];

  const unmatchedExpenses = formatUnmatchedExpenses(splitUnmatchedExpenses, workspaceMembers);

  return { unmatchedExpenses, reconciledStatement };
}

function formatUnmatchedExpenses(
  unmatchedExpenses: Expense[],
  workspaceMembers: { [key: string]: WorkspaceMember },
): ReconciledStatementRow[] {
  const formattedUnmatchedExpenses = unmatchedExpenses.map((expense) => {
    const vendor = Data.vendors.getByKey(expense?.vendor);
    const paymentMethod = Data.paymentMethods.getByKey(expense?.paymentMethod)?.value.name;
    const category = Data.expenseCategories.getByKey(expense?.category)?.value.name;
    const allReports = Data.reports.get();
    const reports = allReports
      .filter((r) => r.expenses.includes(expense?.key ?? ''))
      .map((r) => r.name)
      .join(', ');
    const workspaceMember = workspaceMembers[expense.createdBy];
    const createdByName =
      workspaceMember?.displayName || getUserDisplayName(workspaceMember, 'Anonymous');

    return {
      Total: expense.total,
      Date: expense.date,
      Vendor: vendor?.value.name,
      'Created By': createdByName,
      'Payment Method': paymentMethod,
      Reports: reports,
      Category: category,
      Description: expense.desc ?? '',
      'Expense URL': `${window.location.origin}/expense/${expense?.key}`,
      Attachments: getReceiptsURL(expense),
    };
  });
  return formattedUnmatchedExpenses;
}

async function handleSplitExpenses(params: {
  expenses: Expense[];
  matchedExpenseKeys: string[];
  unreconciledStatements: DoNotFixMeIAmAny[];
  workspaceMembers: { [key: string]: WorkspaceMember };
  totalHeader: string;
  dateHeader: string;
  token: string;
  showTax: boolean;
  dateOrder: DateOrder;
}) {
  let unmatchedExpenses = params.expenses.filter(
    (expense) => !params.matchedExpenseKeys.includes(expense.key),
  );
  const unreconciledStatements = [...params.unreconciledStatements];
  const reconciledStatements: DoNotFixMeIAmAny[] = [];
  const unmatchedExpensesMap: Map<string, Expense[]> = new Map();

  unmatchedExpenses.forEach((expense) => {
    const dateVendor = `${expense.date}${expense.vendor}`;
    const unmatchedExpensesByDateVendor = unmatchedExpensesMap.get(dateVendor) ?? [];
    unmatchedExpensesMap.set(dateVendor, [...unmatchedExpensesByDateVendor, expense]);
  });

  unmatchedExpensesMap.forEach((unmatchedExpensesByDateVendor) => {
    if (unmatchedExpensesByDateVendor.length <= 1) {
      return;
    }
    const total = unmatchedExpensesByDateVendor.reduce((acc, exp) => ({
      ...exp,
      total: acc.total + exp.total,
    })).total;

    const unreconciledStatement = unreconciledStatements.find((unreconciledStatementRow) => {
      const statementTotal = parseCurrencyForComparison(
        unreconciledStatementRow[params.totalHeader],
      );
      if (statementTotal === null) {
        return false;
      }
      return unmatchedExpensesByDateVendor.some((exp) => {
        if (
          compareStatementDateWithExpenseDate(
            exp,
            unreconciledStatementRow,
            params.dateHeader,
            params.dateOrder,
          ) &&
          Math.abs(total - statementTotal) < 0.01
        ) {
          unmatchedExpenses = unmatchedExpenses.filter(
            (unmatchedExp) =>
              !(exp.vendor === unmatchedExp.vendor && exp.date === unmatchedExp.date),
          );
          return true;
        }
        return false;
      });
    });

    const unmatchedExpense = unmatchedExpensesByDateVendor.pop();
    const vendorKey = unmatchedExpense?.vendor;
    const categoryKey = unmatchedExpense?.category;
    const paymentMethodKey = unmatchedExpense?.paymentMethod;
    const allReports = Data.reports.get();
    const reports = allReports
      .filter((r) => r.expenses.includes(unmatchedExpense?.key ?? ''))
      .map((r) => r.name)
      .join(', ');
    const workspaceMember = params.workspaceMembers[unmatchedExpense?.createdBy ?? ''];
    const createdByName =
      workspaceMember?.displayName || getUserDisplayName(workspaceMember, 'Anonymous');

    if (unreconciledStatement) {
      const taxFields = params?.showTax
        ? {
            'Tax*': unmatchedExpense?.tax,
            'Subtotal*': (unmatchedExpense?.total ?? 0) - (unmatchedExpense?.tax ?? 0),
          }
        : {};

      reconciledStatements.push({
        Status: 'Reconciled',
        'Vendor*': Data.vendors.getByKey(vendorKey)?.value.name ?? '',
        'Category*': Data.expenseCategories.getByKey(categoryKey)?.value.name ?? '',
        'Payment Method*': Data.paymentMethods.getByKey(paymentMethodKey)?.value.name ?? '',
        'Reports*': reports,
        'Description*': unmatchedExpense?.desc,
        'Created By*': createdByName,
        'Receipt Date*': unmatchedExpense?.date,
        'Expense URL*': `${window.location.origin}/expense/${unmatchedExpense?.key}`,
        'Attachments*': getReceiptsURL(unmatchedExpense),
        ...taxFields,
        ...unreconciledStatement,
      });
    }
  });

  return { unmatchedExpenses, reconciledStatements };
}

/** returns true if dates are same or close enough
 * */
function compareStatementDateWithExpenseDate(
  expense: Expense,
  statementRow: DoNotFixMeIAmAny,
  dateHeader: string,
  dateOrder: DateOrder,
): boolean {
  const formattedStatementDate = parseStatementDate(statementRow[dateHeader], dateOrder);
  // expense.date is already in 'yyyy-MM-dd' format
  return expense.date === formattedStatementDate;
}

/**
 * Finds the best matching expense based on date proximity.
 * Matches dates within 7 days in the future.
 * Prefers the closest match.
 */
function findBestDateMatch(
  expenses: Expense[],
  statementRow: DoNotFixMeIAmAny,
  dateHeader: string,
  dateOrder: DateOrder,
): Expense | undefined {
  const statementDate = parseStatementDate(statementRow[dateHeader], dateOrder);
  if (!statementDate || !/^\d{4}-\d{2}-\d{2}$/.test(statementDate)) {
    return undefined;
  }

  const validMatches: { expense: Expense; daysDiff: number }[] = [];

  for (const expense of expenses) {
    // expense.date is already in 'yyyy-MM-dd' format, no conversion needed
    if (!expense.date || !/^\d{4}-\d{2}-\d{2}$/.test(expense.date)) {
      continue;
    }

    const daysDiff = calculateDaysDifference(expense.date, statementDate);

    // daysDiff: positive = statement is AFTER expense (future), negative = statement is BEFORE expense (past)
    // Check if within valid range: 0 (same day) to 7 (7 days future)
    if (daysDiff >= 0 && daysDiff <= 7) {
      validMatches.push({
        expense,
        daysDiff,
      });
    }
  }

  if (validMatches.length === 0) {
    return undefined;
  }

  // Sort by days difference (closest first)
  validMatches.sort((a, b) => a.daysDiff - b.daysDiff);

  return validMatches[0]?.expense ?? undefined;
}

function getReceiptsURL(expense: Expense | undefined) {
  if (!expense || !expense?.key || !expense?.receipts.length) {
    return '';
  }
  // Return the first receipt URL directly (same as transaction list)
  // The cloud function URL returns HTML, not a direct image URL
  return expense.receipts[0];
}
